import { AB_TEST, AB_TEST_CONFIG_MAP, FEATURE_FLAG, TRUE_VALUE } from '../apis/eols-features-api/eols-features-api.constants';
import { FeatureFlagDto, FeatureFlagsGroupedDto } from '../apis/eols-features-api/eols-features-api.dtos';
import { Application } from '../apis/eols-app-link/eols-app-link.constants';

// * - eols-features-api marks a flag record as applying to every group by including this
// * - sentinel in `groups`, rather than leaving `groups` empty.
const GLOBAL_FLAG_GROUP = 'ALL';

const isGlobalFlagRecord = (flag: FeatureFlagsGroupedDto): boolean => {
  return flag.groups.length === 0 || flag.groups.includes(GLOBAL_FLAG_GROUP);
};

export const getGroupFeatureFlagWithFallbackToGlobal = (flags: FeatureFlagsGroupedDto[], featureFlag: FEATURE_FLAG, group: string): FeatureFlagsGroupedDto => {

  if (!flags) {
    return null;
  }

  const groupMatch = flags.find((flag) => {
    return flag.featureName === featureFlag
      && group
      && flag.groups.includes(group)
      && !isGlobalFlagRecord(flag);
  });
  if (groupMatch) {
    return groupMatch;
  }
  return flags.find((flag) => {
    return flag.featureName === featureFlag && isGlobalFlagRecord(flag);
  });
};

export const getBooleanFromGroupFeatureFlagWithFallbackToGlobal = (flags: FeatureFlagsGroupedDto[], featureFlag: FEATURE_FLAG, group: string): boolean => {
  let singleAppFlags = flags;
  const hasStudentStudyFlags = flags && flags.some(flag => flag.eolsApp === Application.STUDENT_STUDY);
  const hasOtherAppFlags = flags && flags.some(flag => flag.eolsApp && flag.eolsApp !== Application.STUDENT_STUDY);
  const hasMultipleAppFlags = hasStudentStudyFlags && hasOtherAppFlags;

  if (hasMultipleAppFlags) {
    singleAppFlags = flags.filter(flag => flag.eolsApp === Application.STUDENT_STUDY);
  }

  const flag = getGroupFeatureFlagWithFallbackToGlobal(singleAppFlags, featureFlag, group);
  if (!flag) {
    // Return null here to force explicit setting of feature flags
    return null;
  }
  return flag.featureValue === TRUE_VALUE;
};

export const getFeatureFlagGroups = (flags: FeatureFlagsGroupedDto[], featureName: FEATURE_FLAG, featureValue: string = TRUE_VALUE): string[] => {

  if (!flags || !flags.length) {
    return [];
  }

  const flag = flags.find((featureFlag) => {
    return featureFlag.featureName === featureName
      && featureFlag.featureValue === featureValue
      && featureFlag.groups
      && featureFlag.groups.length;
  });

  if (!flag || !flag.groups) {
    return [];
  }

  return flag.groups;
};

export const getRandomABTestFlavor = (flag: FeatureFlagsGroupedDto, rand = Math.random()) => {
  const possibleFlavors = AB_TEST_CONFIG_MAP[flag.featureName];
  if (!possibleFlavors || !possibleFlavors.length) {
    return null;
  }
  return possibleFlavors[Math.floor(rand * possibleFlavors.length)];
};

export const getABTestFlavor = (props: {
  flag: FEATURE_FLAG;
  appFlags: FeatureFlagsGroupedDto[];
  abFlags: FeatureFlagDto[];
  group: string;
  defaultFlavor: string;
}): string => {

  const {
    flag,
    appFlags,
    abFlags,
    group,
    defaultFlavor
  } = props;

  const appFlag = getGroupFeatureFlagWithFallbackToGlobal(appFlags, flag, group);
  if (appFlag && appFlag.featureValue !== AB_TEST) {
    return appFlag.featureValue;
  }
  const abFlag = abFlags.find((_abFlag) => {
    return _abFlag.featureName === flag;
  });

  if (abFlag) {
    return abFlag.featureValue;
  }
  return defaultFlavor;
};

export const getABPropsForAnalytics = (abTestFlavors: FeatureFlagDto[]): Record<string, string> => {
  if (!abTestFlavors || !abTestFlavors.length) {
    return {};
  }
  return abTestFlavors.reduce((acc, cur) => {
    return {
      ...acc,
      [cur.featureName]: cur.featureValue
    };
  }, {});
};

export const getActiveABTestFlags = (flags: FeatureFlagsGroupedDto[], group: string): FeatureFlagsGroupedDto[] => {

  if (!flags) {
    return null;
  }

  const groupMatches = flags.filter((flag) => {
    return flag.featureValue === AB_TEST && group && flag.groups.includes(group) && !isGlobalFlagRecord(flag);
  });

  const globalMatches = flags.filter((flag) => {
    if (
      groupMatches.some((_flag) => {
        return _flag.featureName === flag.featureName;
      })
    ) {
      return false;
    }
    return flag.featureValue === AB_TEST && isGlobalFlagRecord(flag);
  });

  return [...groupMatches, ...globalMatches];
};

export const isFeatureFlagEnabled = (
  flag: FEATURE_FLAG,
  featureFlagsGrouped: FeatureFlagsGroupedDto[],
  courseSectionId: string,
  app: Application = Application.STUDENT_STUDY
): boolean => {
  let specificAppFlags = featureFlagsGrouped;

  if (app !== Application.STUDENT_STUDY) {
    specificAppFlags = featureFlagsGrouped.filter(featureFlag => featureFlag.eolsApp === app);
  }

  return getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    specificAppFlags,
    flag,
    courseSectionId
  );
};
